"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Calendar, User, Clock } from "lucide-react";



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="space-y-8 animate-pulse">
        <div className="h-12 bg-primary/5 rounded-md w-3/4 mx-auto"></div>
        <div className="h-64 bg-primary/5 rounded-xl w-full"></div>
        <Card className="p-6 md:p-8 shadow-md border-border/50">
          <div className="space-y-4">
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-5/6"></div>
            <div className="h-4 bg-muted rounded w-4/6"></div>
          </div>
        </Card>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <Card className="p-8 text-center shadow-md border-border/50">
        <p className="text-card-foreground font-default">Impossibile caricare l'articolo richiesto.</p>
      </Card>
    );
  }

  const post = posts[0];

  return (
    <article className="space-y-8">
      {}
      <div className="space-y-6 text-center px-4">
        <h1 className="text-3xl md:text-5xl font-bold font-default text-primary leading-tight tracking-tight">
          {post.title}
        </h1>
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
          {post.author && (
            <span className="flex items-center gap-1.5 bg-muted px-3 py-1 rounded-sm border border-border/50">
              <User className="size-4 text-secondary" /> {post.author.name}
            </span>
          )}
          {post.date && (
            <span className="flex items-center gap-1.5 bg-muted px-3 py-1 rounded-sm border border-border/50">
              <Calendar className="size-4 text-secondary" /> {post.date}
            </span>
          )}
          <span className="flex items-center gap-1.5 bg-muted px-3 py-1 rounded-sm border border-border/50">
            <Clock className="size-4 text-secondary" /> 4 min di lettura
          </span>
        </div>
      </div>

      {}
      {post.featuredImage && (
        <div className="rounded-lg overflow-hidden shadow-xl border border-border/50">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto max-h-[60vh] object-cover"
          />
        </div>
      )}

      {}
      <Card className="p-6 md:p-10 shadow-lg border border-border/30 bg-card/50 backdrop-blur-sm">
        <div
          className="prose prose-sm md:prose-base lg:prose-lg max-w-none text-foreground/90 prose-headings:font-default prose-headings:text-primary prose-a:text-secondary hover:prose-a:text-secondary/80 prose-strong:text-primary"
          dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "" }}
        />
      </Card>

      {}
      {post.author && (
        <Card className="p-6 flex flex-col sm:flex-row items-center sm:items-start gap-6 shadow-md border border-border/50 bg-muted/30">
          <Avatar className="size-20 border-2 border-secondary shadow-sm">
            <AvatarImage src={post.author.avatar?.["96"]} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xl font-bold font-default">
              {post.author.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="text-center sm:text-left space-y-2">
            <h3 className="font-bold font-default text-xl text-primary">
              {post.author.name}
            </h3>
            <p className="text-sm text-muted-foreground font-default">
              Esperto in mobilità internazionale e consulenza corporate. Specializzato in visti e procedure di immigrazione per aziende e istituti globali.
            </p>
          </div>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <Card key={i} data-index={i} className="h-64 animate-pulse bg-muted/50 border-none" />
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <Card
          key={post.id}
          data-index={i}
          className="flex flex-col h-full shadow-md hover:shadow-xl transition-all duration-300 border border-border/50 overflow-hidden group bg-card"
        >
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
            </div>
          )}
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-bold font-default text-primary line-clamp-2 group-hover:text-secondary transition-colors">
              {post.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow pb-4">
            <p
              className="text-sm text-muted-foreground line-clamp-3 font-default"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button
                variant="outline"
                className="w-full border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground rounded-none font-default uppercase tracking-wider text-xs"
              >
                Approfondisci
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}



export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };

  const mainPostQuery = new WP_Query(currentWPQueryParams);
  
  
  const relatedPostsQuery = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section data-section-id="235572"
      id="single-post-template-section"
      className="bg-background min-h-screen py-12 md:py-20 px-4 sm:px-6 lg:px-8 text-foreground"
    >
      <div className="max-w-4xl mx-auto space-y-12">
        {}
        <div className="flex justify-start">
          <Link to="/HomePage">
            <Button
              variant="secondary"
              className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md rounded-none px-6 font-default uppercase tracking-tight text-xs"
            >
              <ArrowLeft className="mr-2 size-4" /> Torna alla Home
            </Button>
          </Link>
        </div>

        {}
        <PostsProvider wp_query={mainPostQuery}>
          <SinglePostConsumer />
        </PostsProvider>

        {}
        <div className="pt-16 border-t border-border/50 space-y-8">
          <h2 className="text-2xl md:text-3xl font-bold font-default text-primary text-center uppercase tracking-widest">
            Approfondimenti Correlati
          </h2>
          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}